import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
} from "@mui/material";
import { motion } from "framer-motion";
import GitHubIcon from "@mui/icons-material/GitHub";
import LaunchIcon from "@mui/icons-material/Launch";

interface ProjectCardProps {
  title: string;
  description: string;
  image?: string;
  technologies: string[];
  githubUrl: string;
  liveUrl?: string;
  index: number;
}

export const ProjectCard = ({
  title,
  description,
  image,
  technologies,
  githubUrl,
  liveUrl,
  index,
}: ProjectCardProps) => {
  const onHoverStyle = {
    "&:hover": {
      "& svg": {
        color: "secondary.main",
      },
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      style={{ height: "100%" }}
    >
      <Card
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: "180px",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            transform: "translateY(-8px)",
            boxShadow: (theme) => theme.shadows[4],
          },
        }}
      >
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            justifyContent: "flex-start",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2,
              "@media (max-width: 700px)": {
                flexDirection: "column",
                textAlign: "center",
              },
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: "secondary.main", fontWeight: "bold" }}
            >
              {title}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {technologies.map((tech) => (
              <Chip
                key={tech}
                label={tech}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: "primary.main",
                  color: "text.secondary",
                  "&:hover": {
                    backgroundColor: "primary.main",
                    color: (theme) =>
                      theme.palette.mode === "dark"
                        ? "background.paper"
                        : "white",
                  },
                }}
              />
            ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              mt: "auto",
              pt: 2,
            }}
          >
            <IconButton
              href={githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{ ...onHoverStyle }}
            >
              <GitHubIcon />
            </IconButton>
            {liveUrl && (
              <IconButton
                href={liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{ ...onHoverStyle }}
              >
                <LaunchIcon />
              </IconButton>
            )}
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};
